// packages
import { gql, DocumentNode } from '@apollo/client';
import React from 'react';

// components
import { ButtonBar } from '../../ButtonBar';
import {
	useInvoiceViewButtonBarExportButton,
	invoiceViewButtonBarExportButtonInvoiceFragment,
} from './useInvoiceViewButtonBarExportButton/useInvoiceViewButtonBarExportButton';
import {
	useInvoiceViewButtonBarPayButton,
	invoiceViewButtonBarPayButtonInvoiceFragment,
} from './useInvoiceViewButtonBarPayButton';
import {
	useInvoiceViewButtonBarApproveButton,
	invoiceViewButtonBarApproveButtonInvoiceFragment,
} from './useInvoiceViewButtonBarApproveButton';
import {
	useInvoiceViewButtonBarSendResendButton,
	invoiceViewButtonBarSendResendButtonInvoiceFragment,
} from './useInvoiceViewButtonBarSendResendButton';
import {
	invoiceViewButtonBarVoidButtonInvoiceFragment,
	useInvoiceViewButtonBarVoidButton,
} from './useInvoiceViewButtonBarVoidButton';
import { InvoiceViewButtonBarInvoiceFragment } from '#types';
import { Differences } from '../../../lib/helpers/deepCompareAndDiff';

const invoiceViewButtonBarInvoiceFragment = gql`
	fragment invoiceViewButtonBarInvoice on Invoice {
		...invoiceViewButtonBarExportButtonInvoice
		...invoiceViewButtonBarApproveButtonInvoice
		...invoiceViewButtonBarSendResendButtonInvoice
		...invoiceViewButtonBarPayButtonInvoice
		...invoiceViewButtonBarVoidButtonInvoice
	}
	${ invoiceViewButtonBarExportButtonInvoiceFragment }
	${ invoiceViewButtonBarPayButtonInvoiceFragment }
	${ invoiceViewButtonBarApproveButtonInvoiceFragment }
	${ invoiceViewButtonBarSendResendButtonInvoiceFragment }
	${ invoiceViewButtonBarVoidButtonInvoiceFragment }
`;

export type InvoiceViewButtonBarProps = {
	invoice?: InvoiceViewButtonBarInvoiceFragment;
	guestToken?: string;
	setShake: React.Dispatch<React.SetStateAction<boolean>>;
	setWarning: React.Dispatch<React.SetStateAction<boolean>>;
	selectedInstallments: {
		id: string;
		dueDate?: string | Date | null;
		amountDue?: number | null;
	}[];
	setSelectedInstallments: React.Dispatch<
	React.SetStateAction<
	{
		id: string;
		dueDate?: string | Date | null;
		amountDue?: number | null;
	}[]
	>
	>;
	requestingUserIsGuest?: boolean;
	requestingUserBaseId?: string;
	invoiceEdits?: Differences;
};

type InvoiceViewButtonBarPropsWithFragment =
	React.FC<InvoiceViewButtonBarProps> & {
		fragments: {
			invoiceViewButtonBarInvoiceFragment: DocumentNode;
		};
	};

export const InvoiceViewButtonBar: InvoiceViewButtonBarPropsWithFragment = ( {
	invoice,
	guestToken,
	setShake,
	setWarning,
	selectedInstallments,
	setSelectedInstallments,
	requestingUserIsGuest,
	requestingUserBaseId,
	invoiceEdits,
} ) => {
	const { button: exportButton } = useInvoiceViewButtonBarExportButton( {
		invoice,
		guestToken,
		invoiceEdits,
	} );
	const { button: payButton, jsx: payButtonJSX } =
		useInvoiceViewButtonBarPayButton( {
			invoice,
			setShake,
			setWarning,
			selectedInstallments,
			setSelectedInstallments,
			requestingUserIsGuest,
			requestingUserBaseId,
			guestToken,
		} );
	const { approveButton, approvedWidget } =
		useInvoiceViewButtonBarApproveButton( {
			invoice,
			guestToken,
			requestingUserBaseId,
		} );
	const { button: sendResendButton, jsx: sendButtonJSX } =
		useInvoiceViewButtonBarSendResendButton( {
			invoice,
			requestingUserBaseId,
			invoiceEdits,
		} );

	const { button: voidButton } = useInvoiceViewButtonBarVoidButton( {
		invoice,
		requestingUserBaseId,
	} );

	if ( !invoice ) {
		return null;
	}

	return (
		<>
			<ButtonBar
				data-cy='invoice-view-button-bar'
				noSideNavVisible={ requestingUserIsGuest }
				prepend={ approvedWidget }
				buttons={ {
					...exportButton,
					...voidButton,
					...payButton,
					...approveButton,
					...sendResendButton,
				} }
			/>
			{ payButtonJSX }
			{ sendButtonJSX }
		</>
	);
};

InvoiceViewButtonBar.fragments = {
	invoiceViewButtonBarInvoiceFragment,
};
